// =============================================================================
//  hidrataCancion.js
//  ABRIR UNA CANCIÓN YA PUBLICADA Y VOLVER A GUARDARLA
//
//  Rob, 07-10-2026: «de entrada veo que Song Library no tiene su botón de varita
//  para editar la canción en Sync Editor como lo tenemos en vídeos, y es
//  genial».
//
//  Lo mismo que ya tenían los vídeos: la varita 🪄 abre la canción entera en el
//  Sync Editor —sus tiempos, sus blancos, sus distractores— y al guardar se
//  escribe ENCIMA en vez de crear una copia. Sin esto, corregir una sílaba
//  desfasada era volver a montar la canción desde cero.
//
//  ── POR QUÉ ESTÁ SUELTO DEL DIBUJO ──────────────────────────────────────────
//  Porque es la parte que puede estropear una canción en silencio: si un blanco
//  cae en la palabra de al lado, la canción sigue abriendo, sigue pintándose
//  bien y el alumno es el que se encuentra el hueco en el sitio equivocado.
//  Suelto se puede medir sin navegador, y sobre todo se puede medir el VIAJE DE
//  IDA Y VUELTA: guardar lo que se hidrató tiene que devolver lo mismo.
//
//  ── LA FORMA QUE SE GUARDA ──────────────────────────────────────────────────
//      sync_data.lines[] = {
//          idx, text, time,
//          words:  ['Dog', 'days', 'are', 'over'],   ← tal cual, en orden
//          blanks: [{ wi: 1, correct: 'days', choices: ['days','nights','weeks'] }]
//      }
//
//  `wi` es la posición dentro de `words`, así que **las palabras guardadas
//  mandan**. Volver a partir `text` por los espacios daría casi siempre lo
//  mismo… y «casi siempre» es justo lo que no sirve: el día que no coincida, el
//  blanco se corre de sitio y nadie se entera.
// =============================================================================

/** Las que el editor pone de relleno antes de pedirle los distractores a la IA. */
const DE_RELLENO = new Set(['wrong1', 'wrong2', 'wrong3']);

/**
 * Una canción del servidor → lo que necesita el Sync Editor para pintarla.
 *
 * @param cancion   lo que devuelve `GET /api/admin/sync-editor/{id}`
 * @param creaLinea el constructor de líneas del editor, `createLineObj`. Se pasa
 *                  en vez de importarse para que esto no arrastre media pantalla
 *                  —ni React— y se pueda medir en una consola pelada.
 */
export function hidrataCancion(cancion, creaLinea) {
    const c = cancion || {};
    const crudas = Array.isArray(c.sync_data?.lines) ? c.sync_data.lines : [];

    const lines = crudas.map((cruda, i) => {
        const idx = Number.isFinite(Number(cruda?.idx)) ? Number(cruda.idx) : i;
        const texto = typeof cruda?.text === 'string' ? cruda.text : '';
        const tiempo = Number(cruda?.time);

        //  Las palabras guardadas mandan. Si no vinieran —una canción vieja, de
        //  antes de que se guardaran— se parte el texto, que es de donde
        //  salieron en su día.
        const guardadas = Array.isArray(cruda?.words)
            ? cruda.words.map(String).filter((w) => w.length > 0)
            : null;

        const base = creaLinea(
            idx,
            guardadas && guardadas.length ? guardadas.join(' ') : texto,
            Number.isFinite(tiempo) ? tiempo : 0,
        );

        //  Los blancos, por su posición.
        const porSitio = new Map();
        for (const b of (Array.isArray(cruda?.blanks) ? cruda.blanks : [])) {
            const wi = Number(b?.wi);
            if (Number.isInteger(wi) && wi >= 0) porSitio.set(wi, b);
        }

        return {
            ...base,
            //  El texto que se enseña es el que se guardó, no el que sale de
            //  volver a juntar las palabras: ahí se perdería el espaciado.
            text: texto || base.text,
            words: base.words.map((w) => {
                const b = porSitio.get(w.wi);
                if (!b) return w;

                const correcta = typeof b.correct === 'string' && b.correct
                    ? b.correct
                    : w.clean;

                //  Lo guardado son los distractores Y la correcta, revueltos.
                //  Dentro del editor `choices` son SÓLO los distractores, así
                //  que al volver se le quita la correcta. Dejarla dentro haría
                //  que la IA la contara como distractor suyo y acabaría
                //  ofreciéndole al alumno la respuesta dos veces.
                const distractores = (Array.isArray(b.choices) ? b.choices : [])
                    .filter((x) => typeof x === 'string' && x && x !== correcta
                        && !DE_RELLENO.has(x));

                return {
                    ...w,
                    clean: correcta,
                    isBlank: true,
                    choices: distractores,
                    //  Dos o más: es el mismo listón con el que se guardaron.
                    //  Con menos, el editor lo pinta como «le falta la IA», que
                    //  es la verdad.
                    hasDistractors: distractores.length >= 2,
                };
            }),
        };
    });

    return {
        id: c.id ?? null,
        videoId: c.youtube_id || '',
        title: c.title || '',
        artist: c.artist || '',
        category: c.category || 'pop',
        gameDifficulty: c.difficulty || 'intermediate',
        publicada: c.is_published !== false,
        lines,
        lineasLeidas: lines.length,
        blancosLeidos: lines.reduce(
            (n, l) => n + l.words.filter((w) => w.isBlank).length, 0),
    };
}

/**
 * El viaje de vuelta: las líneas del editor → el `sync_data` que se guarda.
 *
 * Estaba escrito dentro del botón de guardar. Se saca aquí para que viva al
 * lado de su contrario y se pueda medir el viaje entero: hidratar lo guardado y
 * volver a guardarlo tiene que dar lo mismo. Mientras estuvo suelto no había
 * forma de comprobarlo más que publicando una canción y mirándola.
 *
 * @param revuelve  cómo se barajan las opciones. Se puede pasar otra para medir
 *                  esto sin que el azar decida si la prueba pasa.
 */
export function sincronizaParaGuardar(lines, revuelve = barajaAlAzar) {
    return {
        lines: (Array.isArray(lines) ? lines : []).map((l) => ({
            idx: l.idx,
            text: l.text,
            time: l.time || 0,
            words: (l.words || []).map((w) => w.original),
            blanks: (l.words || []).filter((w) => w.isBlank).map((w) => {
                const reales = (w.choices || []).filter(
                    (c) => !DE_RELLENO.has(c) && c !== w.clean);
                //  Con menos de dos distractores no se guarda ninguno: tres
                //  opciones de las que dos son la misma palabra no es un
                //  ejercicio, es un regalo.
                const todas = reales.length >= 2 ? [...reales, w.clean] : [w.clean];
                return { wi: w.wi, correct: w.clean, choices: revuelve(todas) };
            }),
        })),
    };
}

/** Las opciones no pueden salir siempre con la correcta al final. */
function barajaAlAzar(lista) {
    const a = [...lista];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}
